import React, { useEffect, useRef, useState } from 'react';
import PWAInstallButton from './PWAInstallButton';
import NotificationBell from './NotificationBell';
import IncidentReportModal from './IncidentReportModal';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  HomeIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  UsersIcon,
  CreditCardIcon,
  ChartBarIcon,
  Bars3Icon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  LifebuoyIcon,
  FireIcon,
} from '@heroicons/react/24/outline';
import { NavigationItem } from '../types';
import MobileBottomNav from './MobileBottomNav';
import { usePermissions } from '../hooks/usePermissions';
import { ImpersonationBanner } from './platform/ImpersonationBanner';
import TrialBanner from './TrialBanner';

const navigation: NavigationItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Clientes', href: '/clients', icon: UsersIcon },
  { name: 'Plantillas de Planes', href: '/plan-templates', icon: DocumentTextIcon },
  { name: 'Cobros', href: '/cobros', icon: CreditCardIcon },
  { name: 'Ataúdes', href: '/coffins', icon: ArchiveBoxIcon },
  { name: 'Servicios', href: '/service-activations', icon: FireIcon },
  { name: 'Reportes', href: '/reports', icon: ChartBarIcon },
  { name: 'Usuarios', href: '/users', icon: UserGroupIcon },
  { name: 'Configuración', href: '/configuration', icon: Cog6ToothIcon },
];

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const ENABLE_MOBILE_SIDEBAR = false; // ocultar menú lateral en mobile
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const { canAccessModule } = usePermissions();
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  // Edge swipe para abrir sidebar en mobile (desactivado cuando está oculto)
  const touchStartX = useRef<number | null>(null);
  useEffect(() => {
    if (!ENABLE_MOBILE_SIDEBAR) return;
    const onTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (touchStartX.current !== null && touchStartX.current < 24) {
        const dx = e.touches[0].clientX - touchStartX.current;
        if (dx > 40) {
          setSidebarOpen(true);
          touchStartX.current = null;
        }
      }
    };
    const onTouchEnd = () => { touchStartX.current = null; };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart as any);
      document.removeEventListener('touchmove', onTouchMove as any);
      document.removeEventListener('touchend', onTouchEnd as any);
    };
  }, [ENABLE_MOBILE_SIDEBAR]);

  const handleLogout = () => {
    logout();
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <>
      <ImpersonationBanner />
      <div className="h-screen flex overflow-hidden bg-background-primary">
      {/* Mobile sidebar (oculto en mobile si ENABLE_MOBILE_SIDEBAR = false) */}
      {ENABLE_MOBILE_SIDEBAR && (
        <div className={`fixed inset-0 flex z-50 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-md transition-opacity animate-fade-in" 
            onClick={() => setSidebarOpen(false)} 
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-gradient-to-b from-white via-gray-50 to-white shadow-2xl backdrop-blur-xl">
            {/* ...contenido del sidebar móvil existente... */}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className={`flex flex-col transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'w-20' : 'w-64'}`}>
          <div className="flex flex-col h-0 flex-1 bg-[#0f1117] border-r border-slate-800/60 shadow-[6px_0_24px_-4px_rgba(0,0,0,0.4)]">
            <div className="flex-1 flex flex-col pt-6 pb-3 overflow-y-auto">
              {/* Header with toggle button */}
              <div className={`flex items-center flex-shrink-0 mb-8 transition-all duration-300 ${sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-5'}`}>
                <div className={`flex items-center transition-all duration-300 ${sidebarCollapsed ? 'justify-center' : 'space-x-3'}`}>
                  <div className={`${sidebarCollapsed ? 'h-11 w-11' : 'h-11 w-11'} bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg ring-1 ring-white/10 transition-all duration-300`}>
                    <span className="text-white font-bold text-base tracking-tight">SF</span>
                  </div>
                  {!sidebarCollapsed && (
                    <div className="transition-all duration-300">
                      <h1 className="text-lg font-semibold text-white tracking-tight">Sistema Funeraria</h1>
                      <p className="text-xs text-slate-300 font-normal mt-0.5">Operacion Administrativa</p>
                    </div>
                  )}
                </div>
                {!sidebarCollapsed && (
                  <button
                    onClick={toggleSidebar}
                    className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1 transition-all duration-200"
                    title="Colapsar menú"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Collapsed toggle button */}
              {sidebarCollapsed && (
                <div className="flex justify-center mb-8">
                  <button
                    onClick={toggleSidebar}
                    className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1 transition-all duration-200"
                    title="Expandir menú"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              {/* Navigation */}
              <nav className={`flex-1 space-y-1 ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
                {navigation.map((item, index) => {
                  const isActive = location.pathname === item.href;
                  const moduleName = item.href.replace('/', '') || 'dashboard';
                  
                  // Verificar si el usuario puede acceder a este módulo
                  if (!canAccessModule(moduleName)) {
                    return null;
                  }

                  // Agrupar lógicamente para divisores visuales
                  const showDivider = index > 0 && (
                    (item.name === 'Facturas' && navigation[index - 1].name === 'Plantillas de Planes') ||
                    (item.name === 'Reportes' && navigation[index - 1].name === 'Servicios')
                  );

                  return (
                    <React.Fragment key={item.name}>
                      {showDivider && !sidebarCollapsed && (
                        <div className="mx-3 my-3 h-px bg-gradient-to-r from-transparent via-slate-700/60 to-transparent"></div>
                      )}
                      <Link
                        to={item.href}
                        className={`${
                          isActive
                            ? 'bg-primary-600/20 text-white'
                            : 'text-slate-400 hover:text-slate-100 hover:bg-white/5'
                        } group flex items-center ${sidebarCollapsed ? 'justify-center px-3 py-3' : 'px-3 py-2.5'} text-sm font-medium rounded-lg transition-all duration-150 relative`}
                        title={sidebarCollapsed ? item.name : ''}
                      >
                        {/* Indicador activo — barra izquierda con indigo */}
                        {isActive && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-primary-400 rounded-r-full"></div>
                        )}

                        {React.createElement(item.icon as any, {
                          className: `${
                            isActive
                              ? 'text-primary-300'
                              : 'text-slate-500 group-hover:text-slate-300'
                          } relative z-10 flex-shrink-0 h-5 w-5 transition-colors duration-150 ${sidebarCollapsed ? '' : 'mr-3'}`,
                          strokeWidth: isActive ? 2.2 : 1.8,
                        })}
                        {!sidebarCollapsed && (
                          <span className={`relative z-10 ${isActive ? 'font-semibold text-white' : 'font-medium'}`}>
                            {item.name}
                          </span>
                        )}
                        
                        {/* Tooltip for collapsed state */}
                        {sidebarCollapsed && (
                          <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-md opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                            {item.name}
                            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-1.5 h-1.5 bg-slate-900 rotate-45"></div>
                          </div>
                        )}
                      </Link>
                    </React.Fragment>
                  );
                })}
              </nav>
            </div>
            
            {/* User section */}
            <div className={`flex-shrink-0 flex border-t border-slate-800/60 bg-black/20 ${sidebarCollapsed ? 'p-3' : 'p-4'}`}>
                  {sidebarCollapsed ? (
                <div className="flex flex-col items-center w-full space-y-2.5">
                  <div className="h-10 w-10 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 rounded-xl flex items-center justify-center shadow-md ring-1 ring-slate-200/50 hover:shadow-lg hover:ring-slate-300/50 transition-all duration-300">
                    <UserIcon className="h-5 w-5 text-white" strokeWidth={2 as any} />
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 rounded-lg text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:ring-offset-1 transition-all duration-200"
                    title="Cerrar sesión"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" strokeWidth={2 as any} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center w-full">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 rounded-xl flex items-center justify-center shadow-md ring-1 ring-slate-200/50 hover:shadow-lg hover:ring-slate-300/50 transition-all duration-300">
                      <UserIcon className="h-5 w-5 text-white" strokeWidth={2 as any} />
                    </div>
                  </div>
                  <div className="ml-3 flex-1 min-w-0 transition-all duration-300">
                    <p className="text-sm font-semibold text-slate-100 truncate">{user?.name}</p>
                    <p className="text-xs text-slate-400 font-normal capitalize truncate">{user?.role}</p>
                    {user?.tenant?.name && (
                      <p className="text-[11px] text-slate-500 truncate mt-0.5" title={user.tenant.slug}>
                        {user.tenant.name}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setSupportModalOpen(true)}
                      className="mt-2 text-left text-[11px] font-medium text-sky-300/90 hover:text-sky-200 underline-offset-2 hover:underline"
                    >
                      Soporte e incidencias
                    </button>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="ml-2 flex-shrink-0 p-2 rounded-lg text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:ring-offset-1 transition-all duration-200"
                    title="Cerrar sesión"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" strokeWidth={2 as any} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top bar with hamburger button */}
        <div className="lg:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 flex items-center gap-2">
          {ENABLE_MOBILE_SIDEBAR && (
            <button
              type="button"
              className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-xl text-gray-500 hover:text-gray-700 hover:bg-white/80 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500/20 transition-all duration-200 backdrop-blur-sm"
              onClick={() => setSidebarOpen(true)}
              title="Abrir menú"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          )}
          <PWAInstallButton />
        </div>
        
        <main className="flex-1 relative overflow-y-auto focus:outline-none pb-24 lg:pb-0">
          <div className="h-full flex flex-col">
            <div className="flex-1 px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
              <div className="hidden lg:flex justify-end mb-2 items-center gap-3">
                <NotificationBell />
                <PWAInstallButton />
              </div>
              <div className="animate-fade-in-up h-full">
                <TrialBanner />
                {children}
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Mobile bottom navigation */}
      <div className="lg:hidden h-16" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
      <MobileBottomNav
        items={navigation.filter((item) => canAccessModule(item.href.replace('/', '') || 'dashboard'))}
        onLogout={handleLogout}
      />

      <IncidentReportModal isOpen={supportModalOpen} onClose={() => setSupportModalOpen(false)} />

      <button
        type="button"
        onClick={() => setSupportModalOpen(true)}
        className="fixed bottom-24 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-700/80 bg-slate-900 text-white shadow-lg shadow-slate-900/30 transition hover:bg-slate-800 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 lg:bottom-8"
        title="Soporte e incidencias"
        aria-label="Abrir soporte e incidencias"
      >
        <LifebuoyIcon className="h-6 w-6" aria-hidden />
      </button>
      </div>
    </>
  );
};

export default Layout;
